import React from 'react';
import { User } from '../types';
import { useTheme } from '../contexts/ThemeContext';

interface UserListProps {
  users: User[];
  loading: boolean;
  onEdit: (user: User) => void;
  onDelete: (id: string) => void;
}

export function UserList({ users, loading, onEdit, onDelete }: UserListProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  // Edit button: cores diferentes para dark/light para garantir contraste
  const editBg = isDark ? 'rgba(105, 110, 119, 0.1)' : 'rgba(139, 141, 152, 0.1)';
  const editBgHover = isDark ? 'rgba(105, 110, 119, 0.2)' : 'rgba(139, 141, 152, 0.2)';
  const editIconColor = isDark ? '#EDEEF0' : '#1C2024'; // Branco no dark, escuro no light
  // Delete button: mesma cor em ambos os temas (rosa claro funciona bem)
  const deleteBg = 'rgba(229, 70, 102, 0.15)';
  const deleteBgHover = 'rgba(229, 70, 102, 0.2)';
  const deleteIconColor = '#FF949D'; // Rosa claro funciona bem em ambos
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="text-center py-12">
        <p style={{ color: 'var(--text-muted)' }}>Nenhum usuário encontrado</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-[var(--border-color-soft)] transition-colors" style={{ borderColor: 'var(--border-color-soft)' }}>
        <thead
          style={{
            backgroundColor: 'var(--bg-secondary)',
            border: isDark
              ? '1px solid rgba(255, 255, 255, 0.08)'
              : '1px solid rgba(15, 23, 42, 0.12)',
            borderRadius: '12px 12px 0 0',
            boxShadow: isDark
              ? '0 8px 20px rgba(0, 0, 0, 0.35)'
              : '0 10px 24px rgba(15, 23, 42, 0.18)',
            borderBottom: isDark
              ? '1px solid rgba(255, 255, 255, 0.08)'
              : '1px solid rgba(15, 23, 42, 0.12)'
          }}
        >
          <tr>
            <th className="px-6 py-3 text-left text-sm font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
              Nome
            </th>
            <th className="px-6 py-3 text-left text-sm font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
              E-mail
            </th>
            <th className="px-6 py-3 text-left text-sm font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
              Papel
            </th>
            <th className="px-6 py-3 text-left text-sm font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
              Status
            </th>
            <th className="px-6 py-3 text-left text-sm font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
              Último Login
            </th>
            <th className="px-6 py-3 text-right text-sm font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
              Ações
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--border-color-soft)] transition-colors" style={{ 
          backgroundColor: 'var(--bg-card)',
          borderColor: 'var(--border-color-soft)'
        }}>
          {users.map((user) => (
            <tr key={user.id} className="transition-colors" 
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{user.nome}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm" style={{ color: 'var(--text-primary)' }}>{user.email}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  user.role === 'ADMIN'
                    ? 'bg-purple-100 text-purple-800'
                    : 'bg-blue-100 text-blue-800'
                }`}>
                  {user.role === 'ADMIN' ? 'Administrador' : 'Usuário'}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  user.ativo
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {user.ativo ? 'Ativo' : 'Inativo'}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                {user.ultimoLogin
                  ? new Date(user.ultimoLogin).toLocaleString('pt-BR')
                  : 'Nunca'
                }
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <div className="flex justify-end gap-1">
                  <button
                    onClick={() => onEdit(user)}
                    className="inline-flex items-center justify-center h-6 w-6 p-0 rounded-lg transition-all duration-200 ease-in-out focus:outline-none"
                    title="Editar"
                    aria-label={`Editar usuário ${user.nome}`}
                    style={{
                      background: editBg,
                      color: editIconColor
                    }}
                    onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.currentTarget.style.background = editBgHover;
                    }}
                    onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.currentTarget.style.background = editBg;
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-4 w-4"
                      aria-hidden="true"
                    >
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => onDelete(user.id)}
                    className="inline-flex items-center justify-center h-6 w-6 p-0 rounded-lg transition-all duration-200 ease-in-out focus:outline-none"
                    title="Excluir"
                    aria-label={`Excluir usuário ${user.nome}`}
                    style={{
                      background: deleteBg,
                      color: deleteIconColor
                    }}
                    onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.currentTarget.style.background = deleteBgHover;
                    }}
                    onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.currentTarget.style.background = deleteBg;
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-4 w-4"
                      aria-hidden="true"
                    >
                      <path d="M3 6h18" />
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                      <path d="M10 11v6" />
                      <path d="M14 11v6" />
                      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}